import React, { useEffect, useState } from 'react'

import { EmptyState, Icon } from '@clientbase/clientbase-library'
import {
  Box,
  Checkbox,
  TableBody,
  TableContainer,
  TableHead,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material'
import { EligiblePaymentItem } from 'models/Receivables'
import moment from 'moment'
import { ReceivablesService } from 'services/ReceivablesService'

import { ButtonRounded } from 'components/button'
import InfoList, { Info } from 'components/InfoList/InfoList'
import MobileCardList, {
  resolveMobileCards
} from 'components/Table/MobileCardList'
import { HeadCell } from 'components/Table/Table.interface'
import {
  Table as EnhancedTable,
  TableCell as EnhancedTableCell,
  TableHeadCell,
  TableRow as EnhancedTableRow
} from 'components/Table/Table.styles'

import { formatMoney } from 'utils'

import { theme } from 'styles/theme'

import * as S from '../../ModalBalance.styles'

const PAYMENT_TYPE_LABEL: Record<string, string> = {
  credit_card: 'Cartão de crédito',
  credit_card_recurring: 'Cartão recorrente',
  credit_card_installments: 'Cartão parcelado',
  boleto: 'Boleto',
  pix: 'Pix'
}

const HEADER: HeadCell[] = [
  { id: 'customer', label: 'Cliente' },
  { id: 'date_paid', label: 'Data Pagamento' },
  { id: 'due_date', label: 'Data Vencimento' },
  { id: 'gross_amount', label: 'Valor Bruto' },
  { id: 'fee', label: 'Tarifa' },
  { id: 'amount', label: 'Valor Líquido' },
  { id: 'payment_type', label: 'Método' },
  { id: 'actions', label: '' }
]

interface RequestAntecipationProps {
  onSelectionChange: (uuids: string[]) => void
  onOpenBilling: (uuid: string) => void
}

export const RequestAntecipation = ({
  onSelectionChange,
  onOpenBilling
}: RequestAntecipationProps) => {
  const [loading, setLoading] = useState(true)
  const [records, setRecords] = useState<EligiblePaymentItem[]>([])
  const [selected, setSelected] = useState<string[]>([])
  const isMobile = useMediaQuery('(max-width: 600px)', { noSsr: false })

  useEffect(() => {
    const fetch = async () => {
      setLoading(true)
      const { records } = await ReceivablesService.getEligiblePayments()
      setRecords(records)
      setLoading(false)
    }
    fetch()
  }, [])

  useEffect(() => {
    onSelectionChange(selected)
  }, [selected, onSelectionChange])

  const today = moment().format('YYYY-MM-DD')
  const isDisabled = (item: EligiblePaymentItem) =>
    moment(item.date_paid).format('YYYY-MM-DD') === today

  const selectableRecords = records.filter((r) => !isDisabled(r))
  const allSelected =
    selectableRecords.length > 0 && selected.length === selectableRecords.length

  const handleSelectAll = () => {
    const next = allSelected ? [] : selectableRecords.map((r) => r.uuid)
    setSelected(next)
  }

  const handleToggle = (item: EligiblePaymentItem) => {
    if (isDisabled(item)) return
    setSelected((prev) =>
      prev.includes(item.uuid)
        ? prev.filter((id) => id !== item.uuid)
        : [...prev, item.uuid]
    )
  }

  const selectedRecords = records.filter((r) => selected.includes(r.uuid))
  const totalSelected = selectedRecords.reduce(
    (sum, r) => sum + Number(r.amount),
    0
  )

  const renderCustomer = (item: EligiblePaymentItem) => (
    <>
      <Typography variant="xsBold">{item.customer_name || '-'}</Typography>
      {item.customer_nickname && (
        <Typography variant="xsLight" color="text.secondary">
          {item.customer_nickname}
        </Typography>
      )}
    </>
  )

  const renderBillingAction = (item: EligiblePaymentItem) => (
    <Tooltip
      enterTouchDelay={0}
      arrow
      placement="top"
      title={item.billing_uuid ? 'Detalhes da cobrança' : ''}
    >
      <Box>
        <ButtonRounded
          disabled={!item.billing_uuid}
          onClick={(e) => {
            e.stopPropagation()
            if (item.billing_uuid) onOpenBilling(item.billing_uuid)
          }}
          variant="transparent"
        >
          <Icon
            icon="settings"
            sx={{
              color: item.billing_uuid
                ? theme.palette.neutral[200]
                : theme.palette.neutral[500]
            }}
          />
        </ButtonRounded>
      </Box>
    </Tooltip>
  )

  const infos: Info[] = [
    {
      title: 'Selecionados',
      subtitle: String(selected.length)
    },
    {
      title: 'Total a antecipar',
      subtitle: formatMoney(totalSelected)
    }
  ]

  if (loading) {
    return (
      <Box p={2}>
        <Box
          height={60}
          sx={{ mb: 2, bgcolor: 'neutral.800', borderRadius: 1 }}
        />
        <Box height={400} sx={{ bgcolor: 'neutral.800', borderRadius: 1 }} />
      </Box>
    )
  }

  if (!records.length) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="100%"
      >
        <Box maxWidth="600px" width="100%">
          <EmptyState
            title="Nenhum recebível elegível"
            message="Não há recebíveis disponíveis para antecipação. Lembre-se que só é possível antecipar recebíveis pagos há mais de 1 dia."
          />
        </Box>
      </Box>
    )
  }

  const mobileCards = resolveMobileCards(true, HEADER)

  if (isMobile && mobileCards) {
    return (
      <Box display="flex" flexDirection="column" height="100%" gap={2}>
        <InfoList infos={infos} disableBoxShadow />
        <MobileCardList
          rows={records.map((item) => ({
            uuid: item.uuid,
            // Recebível pago hoje não pode ser antecipado: o card fica
            // esmaecido e ignora o toque, como a linha da tabela.
            clickable: !isDisabled(item),
            customer: renderCustomer(item),
            date_paid: item.date_paid
              ? moment(item.date_paid).format('DD/MM/YYYY')
              : '',
            due_date: item.due_date
              ? moment(item.due_date).format('DD/MM/YYYY')
              : '',
            gross_amount: formatMoney(Number(item.gross_amount)),
            fee: formatMoney(Number(item.gross_amount) - Number(item.amount)),
            amount: formatMoney(Number(item.amount)),
            payment_type:
              PAYMENT_TYPE_LABEL[item.payment_type] || item.payment_type,
            actions: renderBillingAction(item)
          }))}
          config={mobileCards}
          select={{
            useSelect: true,
            selected,
            setSelected: (value) => {
              const next =
                typeof value === 'function'
                  ? value({ useSelect: true, selected })
                  : value
              setSelected(next.selected)
            }
          }}
          hiddePagination
        />
      </Box>
    )
  }

  return (
    <Box display="flex" flexDirection="column" height="100%" gap={2}>
      <InfoList infos={infos} disableBoxShadow />

      <S.TableWrapper>
        <TableContainer>
          <EnhancedTable>
            <TableHead>
              <EnhancedTableRow>
                <EnhancedTableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    checked={allSelected}
                    indeterminate={
                      selected.length > 0 &&
                      selected.length < selectableRecords.length
                    }
                    onChange={handleSelectAll}
                  />
                </EnhancedTableCell>
                <TableHeadCell>Cliente</TableHeadCell>
                <TableHeadCell>Data Pagamento</TableHeadCell>
                <TableHeadCell>Data Vencimento</TableHeadCell>
                <TableHeadCell align="right">Valor Bruto</TableHeadCell>
                <TableHeadCell align="right">Tarifa</TableHeadCell>
                <TableHeadCell align="right">Valor Líquido</TableHeadCell>
                <TableHeadCell>Método</TableHeadCell>
                <TableHeadCell />
              </EnhancedTableRow>
            </TableHead>
            <TableBody>
              {records.map((item, index) => {
                const isSelected = selected.includes(item.uuid)
                const disabled = isDisabled(item)
                const fee = Number(item.gross_amount) - Number(item.amount)
                const isLastRow = index === records.length - 1

                return (
                  <Tooltip
                    key={item.uuid}
                    title={
                      disabled
                        ? 'Recebíveis pagos no mesmo dia não podem ser antecipados'
                        : ''
                    }
                    placement="top"
                    enterTouchDelay={0}
                  >
                    <EnhancedTableRow
                      hoverInRow={!disabled}
                      clickable={!disabled}
                      isLastRow={isLastRow}
                      hideLastRowSeparator
                      onClick={() => handleToggle(item)}
                      sx={{ opacity: disabled ? 0.4 : 1 }}
                    >
                      <EnhancedTableCell component="th" scope="row">
                        <Checkbox
                          color="primary"
                          checked={isSelected}
                          disabled={disabled}
                          onChange={() => handleToggle(item)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </EnhancedTableCell>
                      <EnhancedTableCell clickable={!disabled}>
                        {renderCustomer(item)}
                      </EnhancedTableCell>
                      <EnhancedTableCell clickable={!disabled}>
                        {item.date_paid
                          ? moment(item.date_paid).format('DD/MM/YYYY')
                          : '-'}
                      </EnhancedTableCell>
                      <EnhancedTableCell clickable={!disabled}>
                        {item.due_date
                          ? moment(item.due_date).format('DD/MM/YYYY')
                          : '-'}
                      </EnhancedTableCell>
                      <EnhancedTableCell align="right" clickable={!disabled}>
                        {formatMoney(Number(item.gross_amount))}
                      </EnhancedTableCell>
                      <EnhancedTableCell align="right" clickable={!disabled}>
                        {formatMoney(fee)}
                      </EnhancedTableCell>
                      <EnhancedTableCell align="right" clickable={!disabled}>
                        {formatMoney(Number(item.amount))}
                      </EnhancedTableCell>
                      <EnhancedTableCell clickable={!disabled}>
                        {PAYMENT_TYPE_LABEL[item.payment_type] ||
                          item.payment_type}
                      </EnhancedTableCell>
                      <EnhancedTableCell>
                        {renderBillingAction(item)}
                      </EnhancedTableCell>
                    </EnhancedTableRow>
                  </Tooltip>
                )
              })}
            </TableBody>
          </EnhancedTable>
        </TableContainer>
      </S.TableWrapper>
    </Box>
  )
}
